import * as THREE from 'three'
import {
  Project,
  Printedcircuitboard,
  PrintedcircuitboardEdge,
  CircularClippingElement,
  CircularEngravingElement,
  RectangularClippingElement,
  RectangularEngravingElement,
  ContentEdge
} from 'src/editor/types'
import { Vector2 } from 'three'

export const getPrintedcircuitboardPlateBoundings = ({
  width: { value: width },
  length: { value: length },
  thickness: { value: thickness }
}) => {
  const x = length / 2
  const y = thickness / 2
  const z = width / 2
  return new THREE.Box3(new THREE.Vector3(-x, -y, -z), new THREE.Vector3(x, y, z))
}

export const getPrintedcircuitboardBoundings = ({
  properties: {
    width: { value: width },
    length: { value: length },
    thickness: { value: thickness },
    componentSide: {
      height: { value: componentSideHeight }
    },
    solderSide: {
      height: { value: solderSideHeight }
    }
  }
}: Pick<Printedcircuitboard, 'properties'>) => {
  const size = {
    x: length / 2,
    y: (thickness + componentSideHeight + solderSideHeight) / 2,
    z: width / 2
  }
  const offset = {
    x: 0,
    y: (componentSideHeight - solderSideHeight) / 2,
    z: 0
  }
  return new THREE.Box3(
    new THREE.Vector3(-size.x + offset.x, -size.y + offset.y, -size.z + offset.z),
    new THREE.Vector3(size.x + offset.x, size.y + offset.y, size.z + offset.z)
  )
}

export const getPrintedcircuitboardEdgeBoundings = ({
  node: {
    properties: {
      width: { value: width },
      length: { value: length },
      thickness: { value: thickness },
      componentSide: {
        height: { value: componentSideHeight }
      },
      solderSide: {
        height: { value: solderSideHeight }
      }
    }
  },
  properties: {
    gap: {
      front: { value: frontGap },
      top: { value: topGap },
      right: { value: rightGap },
      bottom: { value: bottomGap },
      left: { value: leftGap },
      back: { value: backGap }
    }
  }
}: Pick<PrintedcircuitboardEdge, 'node' | 'properties'>) => {
  const size = {
    x: (length + leftGap + rightGap) / 2,
    y: (thickness + componentSideHeight + solderSideHeight + topGap + bottomGap) / 2,
    z: (width + frontGap + backGap) / 2
  }
  const offset = {
    x: (rightGap - leftGap) / 2,
    y: (topGap + componentSideHeight - bottomGap - solderSideHeight) / 2,
    z: (frontGap - backGap) / 2
  }
  return new THREE.Box3(
    new THREE.Vector3(-size.x + offset.x, -size.y + offset.y, -size.z + offset.z),
    new THREE.Vector3(size.x + offset.x, size.y + offset.y, size.z + offset.z)
  )
}

export const getElementBoundings = (
  element:
    | CircularClippingElement
    | CircularEngravingElement
    | RectangularClippingElement
    | RectangularEngravingElement
): THREE.Box2 => {
  switch (element.__typename) {
    case 'CircularClippingElement':
    case 'CircularEngravingElement':
      const radius = (element as CircularClippingElement | CircularEngravingElement).properties
        .radius.value
      return new THREE.Box2(new THREE.Vector2(-radius, -radius), new THREE.Vector2(radius, radius))
    case 'RectangularClippingElement':
    case 'RectangularEngravingElement':
      const {
        width: { value: width },
        height: { value: height }
      } = (element as RectangularClippingElement | RectangularEngravingElement).properties
      return new THREE.Box2(
        new Vector2(width / -2, height / -2),
        new Vector2(width / 2, height / 2)
      )
    default:
      return new THREE.Box2()
  }
}
